import { useReducer } from "react";
import {
  naturalHomeSort,
  type HomeSort,
  type HomeSortField,
  type HomeTab,
} from "../utils/homeBrowserTypes";

export interface HomeBrowserState {
  tab: HomeTab;
  /** Session-only sort per tab (a sort is a "right now" gesture). */
  sortByTab: Record<HomeTab, HomeSort>;
}

export type HomeBrowserAction =
  | { type: "pickTab"; tab: HomeTab }
  /** Set the sort outright — the header SortMenu (field at natural default). */
  | { type: "setSort"; sort: HomeSort }
  /** Column-header click: the active field flips direction, a different
   *  field jumps to its natural default (the old SortableHeader contract). */
  | { type: "sortByField"; field: HomeSortField };

export const initialHomeBrowserState: HomeBrowserState = {
  tab: "all",
  sortByTab: {
    all: naturalHomeSort("modified"),
    recents: naturalHomeSort("modified"),
    canvases: naturalHomeSort("modified"),
    chats: naturalHomeSort("modified"),
    starred: naturalHomeSort("modified"),
  },
};

export function homeBrowserReducer(
  state: HomeBrowserState,
  action: HomeBrowserAction,
): HomeBrowserState {
  switch (action.type) {
    case "pickTab":
      return action.tab === state.tab ? state : { ...state, tab: action.tab };
    case "setSort":
      return {
        ...state,
        sortByTab: { ...state.sortByTab, [state.tab]: action.sort },
      };
    case "sortByField": {
      const current = state.sortByTab[state.tab];
      const next: HomeSort =
        current.field === action.field
          ? {
              field: action.field,
              dir: current.dir === "asc" ? "desc" : "asc",
            }
          : naturalHomeSort(action.field);
      return {
        ...state,
        sortByTab: { ...state.sortByTab, [state.tab]: next },
      };
    }
  }
}

export function useHomeBrowserState() {
  const [state, dispatch] = useReducer(
    homeBrowserReducer,
    initialHomeBrowserState,
  );
  return {
    state,
    dispatch,
    tab: state.tab,
    sort: state.sortByTab[state.tab],
  };
}
